import { http } from "@/composable/httpService";
import { useHandleErrorStatus } from "@/composable/useHandleErrorStatus";
import { defineStore } from "pinia";
import { computed } from "vue";
import { ref } from "vue";

export const useHomeLearningPathStore = defineStore(
  "homeLearningPathStore",
  () => {
    const sortBy = ref("");
    const filterBy = ref("");
    const filterByPrivacy = ref("");
    const initializeLoading = ref(true);
    const loadingLearningPath = ref(false);
    const learningPath = ref([]);
    const totalItems = ref(10);
    const isLastPage = ref(false);
    const loadingGetAssigneeProgress = ref(false);
    const playlistAssignees = ref([]);
    const loadingDelete = ref(false);
    const controller = ref(null);

    const sorts = computed(() => {
      return [
        {
          name: "A-Z",
          value: "a-z",
        },
        {
          name: "Z-A",
          value: "z-a",
        },
      ];
    });
    const filters = computed(() => {
      return [
        {
          name: "Assigned",
          value: "assigned",
        },
        {
          name: "Not Started",
          value: "not-started",
        },
        {
          name: "In Progress",
          value: "in-progress",
        },
        {
          name: "Completed",
          value: "completed",
        },
      ];
    });

    const privacies = computed(() => {
      return [
        {
          name: "Private",
          value: "private",
        },
        {
          name: "Public",
          value: "public",
        },
      ];
    });

    const handleGetParams = computed(() => {
      return {
        total_items: totalItems.value,
        sort_by: sortBy.value,
        filter_by: filterBy.value,
        privacy_by: filterByPrivacy.value,
      };
    });

    const isPlaylistHasAssignees = computed(
      () => playlistAssignees.value.length > 0
    );

    const handleFetchLearningPath = async () => {
      try {
        loadingLearningPath.value = true;
        controller.value?.abort();
        controller.value = new AbortController();
        const { data } = await http().get("/v3/dashboard/learning-paths", {
          params: handleGetParams.value,
          signal: controller.value.signal,
        });
        learningPath.value = data.items;
        isLastPage.value = data.total_count_of_items <= totalItems.value;
      } catch (error) {
        if (error.response?.status) {
          useHandleErrorStatus(error);
        }
      } finally {
        loadingLearningPath.value = false;
        initializeLoading.value = false;
      }
    };

    const abortFetchLearningPath = () => {
      controller.value?.abort();
    }

    const handleGetPlaylistAssigneesProgress = async (playlistId) => {
      try {
        loadingGetAssigneeProgress.value = true;
        const response = await http().get(
          `/v3/playlist/${playlistId}/assignees`
        );
        playlistAssignees.value = response.data;
      } catch (error) {
        useHandleErrorStatus(error);
      } finally {
        loadingGetAssigneeProgress.value = false;
      }
    };

    const handleDeletePlaylist = async (playlistId) => {
      try {
        loadingDelete.value = true;
        await http().delete(`/v3/learning-paths/${playlistId}`);
      } catch (error) {
        useHandleErrorStatus(error);
      } finally {
        loadingDelete.value = false;
      }
    };

    const handleReset = () => {
      sortBy.value = "";
      filterBy.value = "";
      filterByPrivacy.value = "";
      playlistAssignees.value = [];
    };

    const handleResetList = () => {
      isLastPage.value = false;
      totalItems.value = 10;
      learningPath.value = [];
      playlistAssignees.value = [];
    };

    return {
      sortBy,
      filterBy,
      privacies,
      filterByPrivacy,
      learningPath,
      loadingLearningPath,
      sorts,
      filters,
      isLastPage,
      handleGetParams,
      totalItems,
      initializeLoading,
      handleFetchLearningPath,
      handleResetList,
      handleReset,
      handleGetPlaylistAssigneesProgress,
      loadingGetAssigneeProgress,
      playlistAssignees,
      isPlaylistHasAssignees,
      handleDeletePlaylist,
      loadingDelete,
      abortFetchLearningPath
    };
  }
);
